import { KeyRound, Pencil, Power, PowerOff, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { RowAction } from '@nucleus-commerce/admin-kit/index-table';
import type { WebhookEndpoint } from '@nucleus-commerce/api-client/admin';

import type { EndpointAction } from './endpoint-actions-menu';

export interface EndpointRowActionsOptions {
  onAction: (action: EndpointAction, endpoint: WebhookEndpoint) => void;
  /** Endpoint whose status change is in flight; its entries are disabled until it settles. */
  busyId: string | null;
}

/** The endpoints table's row menu: the same entries as `EndpointActionsMenu` on the detail page. */
export function useEndpointRowActions({ onAction, busyId }: EndpointRowActionsOptions) {
  const { t } = useTranslation('webhooks');
  return useMemo<Array<RowAction<WebhookEndpoint>>>(() => {
    const busy = (endpoint: WebhookEndpoint) => endpoint.id === busyId;
    return [
      {
        id: 'edit',
        label: t('actions.edit'),
        icon: <Pencil aria-hidden />,
        disabled: busy,
        onAction: (endpoint) => onAction('edit', endpoint),
      },
      {
        id: 'disable',
        label: t('actions.disable'),
        icon: <PowerOff aria-hidden />,
        disabled: busy,
        hidden: (endpoint) => endpoint.status !== 'active',
        onAction: (endpoint) => onAction('toggle', endpoint),
      },
      {
        id: 'enable',
        label: t('actions.enable'),
        icon: <Power aria-hidden />,
        disabled: busy,
        hidden: (endpoint) => endpoint.status === 'active',
        onAction: (endpoint) => onAction('toggle', endpoint),
      },
      {
        id: 'rotate',
        label: t('actions.rotate'),
        icon: <KeyRound aria-hidden />,
        disabled: busy,
        onAction: (endpoint) => onAction('rotate', endpoint),
      },
      {
        id: 'delete',
        label: t('actions.delete'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        disabled: busy,
        onAction: (endpoint) => onAction('delete', endpoint),
      },
    ];
  }, [busyId, onAction, t]);
}
